// src/views/PublicLayout.js
import React, { useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import appStoreBadge from '../assets/app-store-badge.svg';
import googlePlayBadge from '../assets/google-play-badge.svg';

const PublicLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // --- CONFIGURATION ---
  // These point to your ACTUAL app subdomain
  const APP_LOGIN_URL = "https://app.spatchy.co/login";
  const APP_SIGNUP_URL = "https://app.spatchy.co/pricing"; 

  // App store links (footer placement — campaign tagged)
  const IOS_APP_URL = "https://apps.apple.com/ca/app/spatchy-field-service/id6766078373?ct=website_footer";
  const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=co.spatchy.app&hl=en&referrer=utm_source%3Dspatchy.co%26utm_medium%3Dfooter";

  return (
    <div className="min-h-screen bg-slate-900 text-white font-sans selection:bg-blue-500 selection:text-white">
      
      {/* --- PUBLIC NAVIGATION --- */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-900/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          
          {/* 1. Logo Area */}
          <Link to="/" className="text-2xl font-bold tracking-tighter flex items-center gap-2">
            {/* NOTE: I am using a pure CSS logo here because your JPG has a white background.
               This looks much cleaner on a dark site than a white box.
            */}
            <div className="w-8 h-8 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-900/50">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="text-white">Spatchy</span>
          </Link>

          {/* 2. Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {/* Internal Links (Stay on Marketing Site) */}
            <Link to="/features" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/about" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">About</Link>
            <Link to="/compare/spatchy-vs-jobber" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Compare</Link>
            
            <div className="flex items-center gap-4 ml-4">
              {/* External Link: LOG IN (Goes to app.spatchy.co) */}
              <a 
                href={APP_LOGIN_URL} 
                className="text-sm font-bold text-white hover:text-blue-400 transition-colors"
              >
                Log In
              </a>

              {/* External Link: START TRIAL (Goes to app.spatchy.co) */}
              <a 
                href={APP_SIGNUP_URL} 
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-full transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transform hover:-translate-y-0.5"
              >
                Start Free Trial
              </a>
            </div>
          </div>

          {/* 3. Mobile Menu Toggle */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="md:hidden text-slate-300 hover:text-white p-2"
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* 4. Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-white/10 p-6 space-y-4 animate-in slide-in-from-top-5 duration-200">
            <Link 
                to="/features" 
                onClick={() => setIsMenuOpen(false)}
                className="block text-lg font-medium text-slate-300 hover:text-white"
            >
                Features
            </Link>
            <Link 
                to="/pricing" 
                onClick={() => setIsMenuOpen(false)}
                className="block text-lg font-medium text-slate-300 hover:text-white"
            >
                Pricing
            </Link>

            <Link 
                to="/about" 
                onClick={() => setIsMenuOpen(false)}
                className="block text-lg font-medium text-slate-300 hover:text-white"
            >
                About
            </Link>
            <Link 
                to="/compare/spatchy-vs-jobber" 
                onClick={() => setIsMenuOpen(false)}
                className="block text-lg font-medium text-slate-300 hover:text-white"
            >
                Compare
            </Link>
            
            <hr className="border-white/10 my-4" />
            
            <a 
                href={APP_LOGIN_URL} 
                className="block text-lg font-bold text-white mb-4"
            >
                Log In
            </a>
            <a 
                href={APP_SIGNUP_URL} 
                className="block w-full text-center py-3 bg-blue-600 rounded-lg font-bold text-white shadow-lg"
            >
                Start Free Trial
            </a>
          </div>
        )}
      </nav>

      {/* --- MAIN CONTENT INJECTION POINT --- */}
      <main>
        <Outlet />
      </main>

      {/* --- PUBLIC FOOTER --- */}
      <footer className="bg-slate-950 py-12 border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 bg-gradient-to-tr from-blue-600 to-purple-600 rounded flex items-center justify-center">
                    <span className="text-white font-bold text-xs">S</span>
                </div>
                <h3 className="text-xl font-bold text-white">Spatchy</h3>
            </div>
            <p className="text-slate-400 max-w-xs text-sm leading-relaxed mb-6">
              The operating system for service businesses. Built for trades, teams, and ambitious owners who want to get their time back.
            </p>

            {/* App Store Badges */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a 
                href={IOS_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download Spatchy on the App Store"
                className="inline-block transform hover:-translate-y-0.5 transition-transform"
              >
                <img 
                  src={appStoreBadge} 
                  alt="Download on the App Store" 
                  className="h-10 w-auto"
                />
              </a>
              <a 
                href={ANDROID_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get Spatchy on Google Play"
                className="inline-block transform hover:-translate-y-0.5 transition-transform"
              >
                <img 
                  src={googlePlayBadge} 
                  alt="Get it on Google Play" 
                  className="h-10 w-auto"
                />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="font-bold text-white mb-4">Product</h4>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/features" className="hover:text-blue-400 transition-colors">Features</Link></li>
              <li><Link to="/pricing" className="hover:text-blue-400 transition-colors">Pricing</Link></li>
              <li><Link to="/support" className="hover:text-blue-400 transition-colors">Support</Link></li>
              <li><a href={APP_LOGIN_URL} className="hover:text-blue-400 transition-colors">Log In</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold text-white mb-4">Legal</h4>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-blue-400 transition-colors">Terms of Service</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-blue-400 transition-colors">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-white/10 text-center text-slate-600 text-xs">
          © {new Date().getFullYear()} Spatchy. All rights reserved. Made in Ontario, Canada 🇨🇦
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;